import React, { useEffect } from 'react';
import usePageScrollLock from '../utils/usePageScrollLock';

/**
 * Hinge-style slide panel for editing fields
 * Mobile: Slides in from right (full screen)
 * Desktop: Drops down as modal overlay
 */
export default function SlidePanel({
  isOpen,
  onClose,
  title,
  children,
}) {
  // Prevent body scroll when the panel is open (a real lock — see usePageScrollLock).
  usePageScrollLock(isOpen);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop - desktop only */}
      <div
        className="hidden md:block fixed inset-0 safe-inset bg-black/50 z-40 animate-fadeIn"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`
          fixed z-50
          bg-white

          /* Mobile: full screen, slide from right */
          inset-0
          animate-slideInRight

          /* Desktop: centered modal */
          md:inset-auto
          md:top-1/2 md:left-1/2
          md:-translate-x-1/2 md:-translate-y-1/2
          md:w-full md:max-w-lg
          md:max-h-[80vh]
          md:rounded-none
          md:border-2 md:border-black
          md:animate-fadeIn

          overflow-hidden
          flex flex-col
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-black bg-white">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 font-mono text-sm font-bold uppercase tracking-wider text-black hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>

          </button>

          <h2 className="font-mono text-sm font-bold uppercase tracking-wider text-black pl-2">
            {title}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="font-mono text-sm font-bold uppercase tracking-wider text-black hover:text-gray-600 transition-colors"
          >
            Done
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {children}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-slideInRight {
          animation: slideInRight 0.3s ease-out;
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
}

/**
 * Clickable field row that opens a SlidePanel
 */
export function FieldRow({
  label,
  value,
  placeholder = 'Add...',
  onClick,
  required = false,
  rightElement,
}) {
  const displayValue = value || placeholder;
  const isEmpty = !value;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors text-left"
    >
      <div className="flex-1 min-w-0">
        <p className="font-mono text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
          {label} {required && <span className="text-red-600">*</span>}
        </p>
        <p className={`font-mono text-sm truncate ${isEmpty ? 'text-gray-400' : 'text-black'}`}>
          {displayValue}
        </p>
      </div>

      {rightElement || (
        <svg className="w-5 h-5 text-gray-400 ml-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </button>
  );
}

/**
 * Toggle field row with inline switch
 */
export function ToggleRow({
  label,
  description,
  checked,
  onChange,
  name,
}) {
  const handleToggle = () => {
    if (name && typeof onChange === 'function') {
      onChange({
        target: {
          name,
          type: 'checkbox',
          checked: !checked,
          value: !checked,
        },
      });
    } else {
      onChange(!checked);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-200">
      <div className="flex-1 min-w-0 pr-4">
        <p className="font-mono text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
          {label}
        </p>
        {description && (
          <p className="font-mono text-xs text-gray-400">
            {description}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 flex-shrink-0">
        <span className={`font-mono text-xs font-bold uppercase tracking-wider ${checked ? 'text-black' : 'text-gray-300'}`}>
          {checked ? 'ON' : 'OFF'}
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          onClick={handleToggle}
          className={`
            relative w-12 h-7
            border-2 border-black
            transition-colors duration-200
            ${checked ? 'bg-black' : 'bg-white'}
          `}
        >
          <span
            className={`
              absolute top-0.5 left-0.5
              w-5 h-5
              border-2 border-black
              transition-transform duration-200
              ${checked ? 'translate-x-5 bg-white' : 'translate-x-0 bg-black'}
            `}
          />
        </button>
      </div>
    </div>
  );
}

/**
 * Section header for grouping fields
 */
export function SectionHeader({ title, subtitle }) {
  return (
    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <h2 className="font-mono text-md font-bold uppercase tracking-wider text-gray-700">
        {title}
      </h2>
      {subtitle && (
        <p className="font-mono text-xs text-gray-500 mt-1">
          {subtitle}
        </p>
      )}
    </div>
  );
}
